import { Check, ChevronDown, LayoutDashboard, LogOut, Settings } from "lucide-react";

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AppearanceToggle,
  useAppearanceToggleVisible,
} from "@/components/v2/layout/AppearanceToggle";
import type { ThemePreference } from "@/lib/theme-preference";

export interface UserInfo {
  name: string;
  company: string;
  avatarUrl?: string;
  initials: string;
}

export interface OrgInfo {
  id: string;
  name: string;
  role: string;
}

interface UserProfileMenuProps {
  user: UserInfo;
  onLogOut?: () => void;
  compact?: boolean;
  className?: string;
  /** Storybook / tests: bypasses ThemePreferenceProvider when both are set. */
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (value: ThemePreference) => void | Promise<void>;
  /** List of organisations the user belongs to. Switcher shown only when length > 1. */
  orgs?: OrgInfo[];
  currentOrgId?: string | null;
  onOrgSwitch?: (orgId: string) => void;
  /** Show Admin panel entry in the menu. Pass true when the authenticated user has role='admin'. */
  isAdmin?: boolean;
  /**
   * Presentation:
   * - 'dropdown' (default): avatar trigger → popover dropdown (desktop top bar).
   * - 'inline': flat, always-expanded section for the mobile burger panel.
   *
   * Both variants render the same options (appearance, admin, log out) from a
   * single definition so desktop and mobile stay in lock-step.
   */
  variant?: "dropdown" | "inline";
}

export function UserProfileMenu({
  user,
  onLogOut,
  compact = false,
  className,
  themePreference: themePreferenceProp,
  onThemePreferenceChange: onThemePreferenceChangeProp,
  orgs,
  currentOrgId,
  onOrgSwitch,
  isAdmin = false,
  variant = "dropdown",
}: UserProfileMenuProps) {
  const showOrgSwitcher = orgs && orgs.length > 1;
  const showAppearance = useAppearanceToggleVisible(
    themePreferenceProp,
    onThemePreferenceChangeProp,
  );

  if (variant === "inline") {
    return (
      <section className={cn("flex flex-col gap-1.5", className)}>
        <p className="px-2.5 pt-1 text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground">
          Account
        </p>
        {/* Flat user presentation (no chevron, no dropdown trigger) — name +
            company, mirroring the desktop dropdown header. */}
        <div
          data-testid="project-topbar-mobile-user"
          className="flex items-center gap-3 rounded-[10px] border border-border bg-secondary px-3 py-2.5"
        >
          <Avatar className="size-9">
            {user.avatarUrl ? (
              <AvatarImage src={user.avatarUrl} alt={user.name} />
            ) : null}
            <AvatarFallback className="bg-linear-to-br from-violet-600 to-blue-500 text-[13px] font-semibold text-primary-foreground">
              {user.initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] font-medium text-foreground">
              {user.name}
            </span>
            <span className="truncate text-[11px] text-muted-foreground">
              {user.company}
            </span>
          </div>
        </div>

        {showAppearance ? (
          <div className="px-0.5 py-1">
            <AppearanceToggle
              themePreference={themePreferenceProp}
              onThemePreferenceChange={onThemePreferenceChangeProp}
            />
          </div>
        ) : null}

        <a
          href="/settings"
          data-testid="project-topbar-mobile-settings"
          className="flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Settings className="size-3.5" />
          <span className="flex-1">Settings</span>
        </a>

        {isAdmin ? (
          <a
            href="/admin"
            data-testid="project-topbar-mobile-admin"
            className="flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LayoutDashboard className="size-3.5" />
            <span className="flex-1">Admin panel</span>
          </a>
        ) : null}

        <button
          type="button"
          data-testid="project-topbar-mobile-logout"
          onClick={() => onLogOut?.()}
          className="flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[13px] font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="size-3.5" />
          <span className="flex-1">Log Out</span>
        </button>
      </section>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-2.5 rounded-full border border-border bg-secondary py-1 pl-1.5 pr-3 transition-colors hover:bg-secondary/80 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
          className,
        )}
      >
        <Avatar className="size-8">
          {user.avatarUrl ? (
            <AvatarImage src={user.avatarUrl} alt={user.name} />
          ) : null}
          <AvatarFallback className="bg-linear-to-br from-violet-600 to-blue-500 text-xs font-semibold text-primary-foreground">
            {user.initials}
          </AvatarFallback>
        </Avatar>

        {!compact && (
          <div className="flex flex-col items-start">
            <span className="max-w-[120px] truncate text-xs font-medium text-foreground">
              {user.name}
            </span>
            <span className="max-w-[120px] truncate text-[10px] leading-3 text-muted-foreground">
              {user.company}
            </span>
          </div>
        )}

        <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        // Solid, opaque, clearly-elevated surface. `bg-popover` is already an
        // opaque token, but a full-opacity border + stronger shadow stops the
        // menu reading as translucent when it sits over a same-colour page
        // (light popover on a light page). (ANA-198)
        className="w-[200px] rounded-lg border-border bg-popover shadow-xl"
      >
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-foreground">{user.name}</p>
          <p className="text-xs text-muted-foreground">{user.company}</p>
        </div>
        <DropdownMenuSeparator className="bg-border/30" />
        {showOrgSwitcher ? (
          <>
            <div className="px-2 py-1" onPointerDown={(e) => e.preventDefault()}>
              <p className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Organisation
              </p>
              {orgs!.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-1 py-1.5 text-xs transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    org.id === currentOrgId ? "font-medium text-foreground" : "text-muted-foreground",
                  )}
                  onClick={() => onOrgSwitch?.(org.id)}
                >
                  <span className="size-3 shrink-0">
                    {org.id === currentOrgId && <Check className="size-3" aria-hidden />}
                  </span>
                  <span className="truncate">{org.name}</span>
                </button>
              ))}
            </div>
            <DropdownMenuSeparator className="bg-border/30" />
          </>
        ) : null}
        {showAppearance ? (
          <>
            <div
              className="px-2 py-1.5"
              onPointerDown={(e) => e.preventDefault()}
            >
              <AppearanceToggle
                themePreference={themePreferenceProp}
                onThemePreferenceChange={onThemePreferenceChangeProp}
              />
            </div>
            <DropdownMenuSeparator className="bg-border/30" />
          </>
        ) : null}
        <DropdownMenuItem
          className="cursor-pointer gap-2 text-muted-foreground hover:text-foreground"
          asChild
        >
          <a href="/settings" data-testid="user-menu-settings">
            <Settings className="size-4" />
            Settings
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-border/30" />
        {isAdmin ? (
          <>
            <DropdownMenuItem
              className="cursor-pointer gap-2 text-muted-foreground hover:text-foreground"
              asChild
            >
              <a href="/admin">
                <LayoutDashboard className="size-4" />
                Admin panel
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-border/30" />
          </>
        ) : null}
        <DropdownMenuItem
          className="cursor-pointer gap-2 text-destructive focus:text-destructive"
          onSelect={() => onLogOut?.()}
        >
          <LogOut className="size-4" />
          Log Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
